import React, { createContext, useContext } from 'react';

import { indexOf } from 'ramda';
import { useSelector } from 'react-redux';

import {
  SETTINGS_ACCOUNT_PATH,
  SETTINGS_MEMBERS_PATH,
  SETTINGS_SUBS_PATH,
  SETTINGS_COMPANY_PATH,
  DASHBOARD_PATH,
  SETTINGS_INTEGRATIONS_PATH,
} from 'constants/routes';
import { useAccountPath } from 'hooks/useAccountPath';
import {
  isAccountFulfilledSelector,
  isAccountOwnerSelector,
  isAgentSelector,
  isOwnerSelector,
} from 'store/user/selectors';

import { ACCOUNT_TYPE_OWNER } from '../../../../constants';

export const SETTINGS_STEPS = {
  ACCOUNT_SETTINGS: 'ACCOUNT_SETTINGS',
  COMPANY_SETTING: 'COMPANY_SETTINGS',
  MEMBERS: 'USERS',
  INTEGRATIONS: 'INTEGRATIONS',
  SUBSCRIPTIONS: 'SUBSCRIPTIONS',
};

export const SETTINGS_STEP_PATHS = {
  [SETTINGS_STEPS.ACCOUNT_SETTINGS]: SETTINGS_ACCOUNT_PATH,
  [SETTINGS_STEPS.COMPANY_SETTING]: SETTINGS_COMPANY_PATH,
  [SETTINGS_STEPS.MEMBERS]: SETTINGS_MEMBERS_PATH,
  [SETTINGS_STEPS.INTEGRATIONS]: SETTINGS_INTEGRATIONS_PATH,
  [SETTINGS_STEPS.SUBSCRIPTIONS]: SETTINGS_SUBS_PATH,
};

export const SETTINGS_STEP_NAMES = {
  [SETTINGS_STEPS.ACCOUNT_SETTINGS]: 'Personal Settings',
  [SETTINGS_STEPS.COMPANY_SETTING]: 'Company Settings',
  [SETTINGS_STEPS.MEMBERS]: 'Users',
  [SETTINGS_STEPS.INTEGRATIONS]: 'Integrations',
  [SETTINGS_STEPS.SUBSCRIPTIONS]: (acc) => (acc.type === ACCOUNT_TYPE_OWNER ? 'Subscriptions' : 'Subscription'),
};

export const getNextSettingsStep = (steps, currentStep) => {
  const currentStepIdx = indexOf(currentStep, steps);

  if (currentStepIdx === -1) {
    return null;
  }

  return steps[currentStepIdx + 1] || null;
};

const useSettingsContextData = () => {
  const getPath = useAccountPath();
  const isOwner = useSelector(isOwnerSelector);
  const isAgent = useSelector(isAgentSelector);

  const isAccountOwner = useSelector(isAccountOwnerSelector);
  const isAccountFulfilled = useSelector(isAccountFulfilledSelector);

  const steps = [
    SETTINGS_STEPS.ACCOUNT_SETTINGS,
    (isAgent || isOwner) && SETTINGS_STEPS.COMPANY_SETTING,
    isAccountOwner && SETTINGS_STEPS.MEMBERS,
    isAgent && isAccountOwner && SETTINGS_STEPS.INTEGRATIONS,
    isAgent && isAccountOwner && SETTINGS_STEPS.SUBSCRIPTIONS,
  ].filter(Boolean);

  const getIdForBackButton = () => {
    if (isOwner) return 'SupplierBackToDashboard';
    if (isAgent) return 'AgentBackToDashboard';
    return undefined;
  };

  return {
    steps,
    backButton: {
      available: isAccountFulfilled,
      link: getPath(DASHBOARD_PATH),
      text: 'Back to Dashboard',
      id: getIdForBackButton(),
    },
  };
};

const SettingsContext = createContext(null);
export const useSettingsContext = () => useContext(SettingsContext);

export function SettingsContextProvider({ children }) {
  const settingsContextData = useSettingsContextData();
  return <SettingsContext.Provider value={settingsContextData}>{children}</SettingsContext.Provider>;
}
